import classNames from "classnames";
import { Icon } from "../../icon/Icon";
import { IconType } from "../../icon/IconType";
import { IconSize } from "../../icon/IconSize";
import ReactDOM from "react-dom";
import { type MessageType } from "../Message";
import { IconClickable, IconClickableType } from "../../icon/IconClickable";
import { type ReactNode } from "react";
import { styled, Box, GlobalStyles, type Theme } from "~/common/mui";

export interface MessageProps {
  children?: ReactNode;
  type: MessageType;
  className?: string;
  onClose?: () => void;
}

const ICONS = {
  warning: IconType.InformationCircle,
  error: IconType.ErrorCircle,
  info: IconType.InformationCircle,
  success: IconType.Check,
};

const getTypeStyles = (messageType: MessageType, theme: Theme) => {
  switch (messageType) {
    case "error":
      return {
        backgroundColor: theme.palette.error.container,
        color: theme.palette.error.onContainer,
      };
    case "info":
      return {
        backgroundColor: theme.palette.info.container,
        color: theme.palette.info.onContainer,
      };
    case "warning":
      return {
        backgroundColor: theme.palette.warning.container,
        color: theme.palette.warning.onContainer,
      };
    case "success":
    default:
      return {};
  }
};

const MessageContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "messageType",
})<{ messageType: MessageType }>(({ theme, messageType }) => ({
  ...theme.typography.labelLarge,
  color: theme.palette.common.white,
  padding: `${theme.spacing(1)} 0`,
  ...getTypeStyles(messageType, theme),
}));

const ToastIcon = styled(Icon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  flex: "none",
}));

const TextContainer = styled(Box)(({ theme }) => ({
  flex: "1 1",
  paddingRight: theme.spacing(3.75), // 30px
  marginRight: theme.spacing(1.25), // 10px
}));

const DismissButton = styled(IconClickable)(({ theme }) => ({
  marginTop: `-${theme.spacing(1)}`,
  display: "flex",
  alignItems: "center",
  position: "absolute",
  right: 0,
  top: 0,
  color: "inherit",
  "&:hover": {
    color: "inherit",
    background: "inherit",
  },
}));

// Global styles for the toast container - uses theme for white color
const getToastContainerGlobalStyles = (theme: Theme) => ({
  "#reactToastContainer": {
    position: "sticky" as const,
    top: 0,
    zIndex: theme.zIndex.snackbar,
  },
  "#reactToastContainer > * + *": {
    borderTop: `1px solid ${theme.palette.common.white}`,
  },
});

const ToastMessage = (props: MessageProps) => {
  const { type, children, onClose } = props;

  const message = (
    <>
      <GlobalStyles styles={(theme) => getToastContainerGlobalStyles(theme)} />
      <MessageContainer messageType={type} className={props.className}>
        <div className={"container d-flex flex-align-start p-relative"}>
          <ToastIcon type={ICONS[type]} size={IconSize.FONT_20} />
          <TextContainer>{children}</TextContainer>
          <DismissButton
            className={classNames(!onClose && "v-hidden")}
            onIconClick={onClose}
            type={IconType.Close}
            size={IconSize.FONT_24}
            inverted={true}
            clickableType={IconClickableType.INFO}
          />
        </div>
      </MessageContainer>
    </>
  );

  const container = document.getElementById("reactToastContainer");
  if (container) {
    return ReactDOM.createPortal(message, container);
  }
  return null;
};

export default ToastMessage;
